import { supabase } from "@/integrations/supabase/client";
import type {
  ReadingMilestone,
  ReadingMilestoneInput,
  UserMilestoneReward,
  MilestoneAnalytics,
  MilestoneCheckResult,
} from "@/types/readingMilestones";

// ── Transformers ──────────────────────────────────────────────

function transformMilestone(row: any): ReadingMilestone {
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    thresholdMinutes: row.threshold_minutes,
    timeSource: row.time_source,
    promotionId: row.promotion_id,
    notificationTemplateId: row.notification_template_id,
    rewardMessage: row.reward_message,
    badgeIconUrl: row.badge_icon_url,
    sortOrder: row.sort_order,
    isActive: row.is_active,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    createdBy: row.created_by,
    promotionName: row.promotions?.name ?? null,
    notificationTemplateName: row.notification_templates?.name ?? null,
    achieverCount: row.user_milestone_rewards?.[0]?.count ?? 0,
  };
}

function transformReward(row: any): UserMilestoneReward {
  return {
    id: row.id,
    userId: row.user_id,
    milestoneId: row.milestone_id,
    achievedAt: row.achieved_at,
    totalMinutesAtAchievement: row.total_minutes_at_achievement,
    promotionId: row.promotion_id,
    notificationSent: row.notification_sent,
    notificationSentAt: row.notification_sent_at,
    promotionRedeemed: row.promotion_redeemed,
    promotionRedeemedAt: row.promotion_redeemed_at,
    userName: row.profiles?.name ?? undefined,
    userEmail: row.profiles?.email ?? undefined,
    userAvatarUrl: row.profiles?.avatar_url ?? undefined,
    milestoneName: row.reading_milestones?.name ?? undefined,
    milestoneThreshold: row.reading_milestones?.threshold_minutes ?? undefined,
  };
}

// ── Milestones CRUD ───────────────────────────────────────────

export async function fetchReadingMilestones(): Promise<ReadingMilestone[]> {
  const { data, error } = await supabase
    .from("reading_milestones")
    .select(
      `*, promotions(name), notification_templates(name), user_milestone_rewards(count)`
    )
    .order("sort_order", { ascending: true })
    .order("threshold_minutes", { ascending: true });

  if (error) {
    console.error("Error fetching reading milestones:", error);
    throw error;
  }

  return (data || []).map(transformMilestone);
}

export async function fetchReadingMilestone(
  id: number
): Promise<ReadingMilestone> {
  const { data, error } = await supabase
    .from("reading_milestones")
    .select(
      `*, promotions(name), notification_templates(name), user_milestone_rewards(count)`
    )
    .eq("id", id)
    .single();

  if (error) {
    console.error("Error fetching reading milestone:", error);
    throw error;
  }

  return transformMilestone(data);
}

export async function createReadingMilestone(
  input: ReadingMilestoneInput
): Promise<ReadingMilestone> {
  const { data, error } = await supabase
    .from("reading_milestones")
    .insert({
      name: input.name,
      description: input.description ?? null,
      threshold_minutes: input.thresholdMinutes,
      time_source: input.timeSource,
      promotion_id: input.promotionId ?? null,
      notification_template_id: input.notificationTemplateId ?? null,
      reward_message: input.rewardMessage ?? null,
      badge_icon_url: input.badgeIconUrl ?? null,
      sort_order: input.sortOrder ?? 0,
      is_active: input.isActive ?? true,
      created_by: input.createdBy ?? null,
    })
    .select(
      `*, promotions(name), notification_templates(name), user_milestone_rewards(count)`
    )
    .single();

  if (error) {
    console.error("Error creating reading milestone:", error);
    throw error;
  }

  return transformMilestone(data);
}

export async function updateReadingMilestone(
  id: number,
  input: Partial<ReadingMilestoneInput>
): Promise<ReadingMilestone> {
  const updateData: Record<string, unknown> = {};

  if (input.name !== undefined) updateData.name = input.name;
  if (input.description !== undefined)
    updateData.description = input.description;
  if (input.thresholdMinutes !== undefined)
    updateData.threshold_minutes = input.thresholdMinutes;
  if (input.timeSource !== undefined)
    updateData.time_source = input.timeSource;
  if (input.promotionId !== undefined)
    updateData.promotion_id = input.promotionId;
  if (input.notificationTemplateId !== undefined)
    updateData.notification_template_id = input.notificationTemplateId;
  if (input.rewardMessage !== undefined)
    updateData.reward_message = input.rewardMessage;
  if (input.badgeIconUrl !== undefined)
    updateData.badge_icon_url = input.badgeIconUrl;
  if (input.sortOrder !== undefined) updateData.sort_order = input.sortOrder;
  if (input.isActive !== undefined) updateData.is_active = input.isActive;

  const { data, error } = await supabase
    .from("reading_milestones")
    .update(updateData)
    .eq("id", id)
    .select(
      `*, promotions(name), notification_templates(name), user_milestone_rewards(count)`
    )
    .single();

  if (error) {
    console.error("Error updating reading milestone:", error);
    throw error;
  }

  return transformMilestone(data);
}

export async function deleteReadingMilestone(id: number): Promise<void> {
  const { error } = await supabase
    .from("reading_milestones")
    .delete()
    .eq("id", id);

  if (error) {
    console.error("Error deleting reading milestone:", error);
    throw error;
  }
}

// ── User Milestone Rewards ────────────────────────────────────

export async function fetchUserMilestoneRewards(filters?: {
  milestoneId?: number;
  userId?: number;
  notificationSent?: boolean;
  limit?: number;
  offset?: number;
}): Promise<{ data: UserMilestoneReward[]; count: number }> {
  let query = supabase
    .from("user_milestone_rewards")
    .select(
      `*, profiles!user_milestone_rewards_user_id_fkey(name, email, avatar_url), reading_milestones(name, threshold_minutes)`,
      { count: "exact" }
    )
    .order("achieved_at", { ascending: false });

  if (filters?.milestoneId) {
    query = query.eq("milestone_id", filters.milestoneId);
  }
  if (filters?.userId) {
    query = query.eq("user_id", filters.userId);
  }
  if (filters?.notificationSent !== undefined) {
    query = query.eq("notification_sent", filters.notificationSent);
  }
  if (filters?.limit) {
    const from = filters.offset ?? 0;
    query = query.range(from, from + filters.limit - 1);
  }

  const { data, error, count } = await query;

  if (error) {
    console.error("Error fetching user milestone rewards:", error);
    throw error;
  }

  return {
    data: (data || []).map(transformReward),
    count: count ?? 0,
  };
}

// ── Analytics ─────────────────────────────────────────────────

export async function fetchMilestoneAnalytics(): Promise<MilestoneAnalytics[]> {
  // Get all milestones with aggregate counts
  const { data: milestones, error: mError } = await supabase
    .from("reading_milestones")
    .select("id, name, threshold_minutes")
    .order("threshold_minutes", { ascending: true });

  if (mError) {
    console.error("Error fetching milestones for analytics:", mError);
    throw mError;
  }

  const analytics: MilestoneAnalytics[] = [];

  for (const m of milestones || []) {
    const { count: totalAchievers } = await supabase
      .from("user_milestone_rewards")
      .select("*", { count: "exact", head: true })
      .eq("milestone_id", m.id);

    const { count: notificationsSent } = await supabase
      .from("user_milestone_rewards")
      .select("*", { count: "exact", head: true })
      .eq("milestone_id", m.id)
      .eq("notification_sent", true);

    const { count: promotionsRedeemed } = await supabase
      .from("user_milestone_rewards")
      .select("*", { count: "exact", head: true })
      .eq("milestone_id", m.id)
      .eq("promotion_redeemed", true);

    analytics.push({
      milestoneId: m.id,
      milestoneName: m.name,
      thresholdMinutes: m.threshold_minutes,
      totalAchievers: totalAchievers ?? 0,
      notificationsSent: notificationsSent ?? 0,
      promotionsRedeemed: promotionsRedeemed ?? 0,
    });
  }

  return analytics;
}

// ── Manual RPC trigger ────────────────────────────────────────

export async function triggerMilestoneCheck(
  userId: number
): Promise<MilestoneCheckResult> {
  const { data, error } = await supabase.rpc(
    "check_and_grant_reading_milestones",
    { p_user_id: userId }
  );

  if (error) {
    console.error("Error triggering milestone check:", error);
    throw error;
  }

  return data as MilestoneCheckResult;
}
